<script lang="ts">
	import type { SvelteAsr, i18n } from 'types/common'
	import type { HomeWorkOrders$result, HomeData$result } from '$houdini'
	import type { WorkOrderFilters } from './work-order'

	import HomeFilter from '../HomeFilter.svelte'
	import { toLocaleDateString } from '@isoftdata/utility-date-time'
	import { format as formatCurrency } from '@isoftdata/utility-currency'
	import Checkbox from '@isoftdata/svelte-checkbox'
	import Select from '@isoftdata/svelte-select'
	import toTitleCase from 'to-title-case'

	import Table, { type Column, Td } from '@isoftdata/svelte-table'
	import { getContext } from 'svelte'
	import NoItemsForSelectedFilter from 'components/NoItemsForSelectedFilter.svelte'
	import { calculatePercent } from '../home.js'

	interface Props {
		asr: SvelteAsr
		workOrders: HomeWorkOrders$result['workOrders']['items']
		documentTypes: Array<string>
		stores: HomeData$result['stores']
		users: HomeData$result['userAccounts']
		userId: WorkOrderFilters['userId']
		storeId: WorkOrderFilters['storeId']
		showClosed: WorkOrderFilters['showClosed']
		showEstimates: WorkOrderFilters['showEstimates']
		documentType: WorkOrderFilters['documentType']
		dates: WorkOrderFilters['dates']
	}

	let {
		//
		asr,
		dates,
		workOrders,
		documentTypes,
		stores,
		users,
		userId,
		storeId,
		showClosed,
		showEstimates,
		documentType = '',
	}: Props = $props()

	const { t: translate } = getContext<i18n>('i18next')

	const columns: Array<Column<(typeof displayWorkOrders)[number]>> = [
		{ property: 'storeId', name: translate('common:storeNumber', 'Store #'), numeric: true },
		{ property: 'workOrderId', name: translate('home.workOrder.workOrderNumberColumn', 'WO #'), numeric: true },
		{ property: 'date', name: translate('common:date', 'Date'), defaultSortColumn: true, defaultSortDirection: 'DESC' },
		{ property: 'documentType', name: translate('common:type', 'Type') },
		{ property: 'counterperson', name: translate('common:counterperson', 'Counterperson') },
		{ property: 'total', name: translate('common:total', 'Total'), numeric: true },
		{
			property: 'totalJobPartQuantityReady',
			name: translate('home.workOrder.quantityReadyColumn', 'Qty Ready'),
			numeric: true,
		},
		{ property: 'percentReady', name: translate('home.workOrder.percentReadyColumn', '% Ready'), numeric: true },
		{ property: 'notes', name: translate('common:notes', 'Notes') },
		{ property: 'description', name: translate('common:description', 'Description') },
		{ property: 'promisedByDate', name: translate('home.workOrder.promisedByDateColumn', 'Promised By') },
		{ property: 'percentDone', name: translate('home.workOrder.percentDoneColumn', '% Done'), numeric: true },
		{ property: 'status', name: translate('common:status', 'Status') },
	]

	function filterChange() {
		asr.go(
			null,
			{
				userId: userId || null, //undefined will cause the parameter default to kick in, so pass null instead
				storeId,
				showClosed,
				showEstimates,
				documentType,
				fromDate: dates?.from,
				toDate: dates?.to,
			},
			{ inherit: true },
		)
	}

	let displayWorkOrders = $derived(
		workOrders.map(wo => {
			return {
				...wo,
				storeId: wo.store?.id,
				date: wo.date,
				documentType: wo.type?.name || '',
				counterperson: wo.user.name,
				percentReady: calculatePercent(wo.totalJobPartQuantityReady, wo.totalJobPartQuantity),
				notes: wo.notes || '',
				promisedByDate: wo.datePromised,
				percentDone: wo.closed ? 100 : calculatePercent(wo.totalJobsDone, wo.jobCount),
				status: wo.closed
					? 'WO Closed'
					: Object.entries(
							wo.jobs.reduce((acc, job) => {
								return {
									...acc,
									[job.status]: (acc[job.status] || 0) + 1,
								}
							}, {}),
						)
							.map(([status, count]) => `${toTitleCase(status)} (${count})`)
							.join(', '),
			}
		}),
	)
</script>

<div class="row">
	<div class="col-md-3 col-lg-2">
		<HomeFilter
			{stores}
			{users}
			{filterChange}
			dateRangeSetting="defaultWorkOrderHistoryDateRange"
			bind:userId
			bind:storeId
			bind:dates
		>
			{#snippet first()}
				<Select
					emptyValue=""
					label={translate('common:documentType', 'Document Type')}
					emptyText={translate('common:allDocumentTypes', 'All Document Types')}
					bind:value={documentType}
					onchange={() => filterChange()}
				>
					{#each documentTypes as type}
						<option value={type}>{type}</option>
					{/each}
				</Select>
			{/snippet}
			<Checkbox
				label={translate('common:showClosed', 'Show Closed')}
				bind:checked={showClosed}
				onchange={filterChange}
			/>
			<Checkbox
				label={translate('common:showEstimates', 'Show Estimates')}
				bind:checked={showEstimates}
				onchange={filterChange}
			/>
		</HomeFilter>
	</div>

	<div
		class="col-md-9 col-lg-10 tab-pane"
		id="sales"
		role="tab"
	>
		{#if displayWorkOrders.length > 0}
			<Table
				responsive
				hover={false}
				{columns}
				perPageCount={15}
				rows={displayWorkOrders}
			>
				{#snippet children({ row })}
					<tr class:text-body-secondary={row.closed}>
						<Td property="storeId">{row.storeId}</Td>
						<Td property="workOrderId">{row.workOrderId}</Td>
						<Td property="date">{toLocaleDateString(row.date)}</Td>
						<Td property="documentType">{row.documentType}</Td>
						<Td property="counterperson">{row.counterperson}</Td>
						<Td property="total">{formatCurrency(row.total)}</Td>
						<Td property="totalJobPartQuantityReady">{row.totalJobPartQuantityReady}/{row.totalJobPartQuantity}</Td>
						<Td property="percentReady">{row.percentReady}%</Td>
						<Td property="notes">{row.notes}</Td>
						<Td property="description">{row.description}</Td>
						<Td property="promisedByDate">{row.promisedByDate ? toLocaleDateString(row.promisedByDate) : ''}</Td>
						<Td
							property="percentDone"
							class={{ 'text-primary': row.percentDone === 100, 'text-danger': !row.percentDone }}
							>{row.percentDone}%</Td
						>
						<Td property="status">{row.status}</Td>
					</tr>
				{/snippet}
			</Table>
		{:else}
			<NoItemsForSelectedFilter />
		{/if}
	</div>
</div>
